import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Bed, Bath, Square, ArrowRight, Filter, Building2, Lock, LogIn, Search, X, DollarSign, Percent, Clock, Home, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertiesPage() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [marketFilter, setMarketFilter] = useState("all");
  const [listingTypeFilter, setListingTypeFilter] = useState("all");
  const [priceRange, setPriceRange] = useState([0, 20000000]);
  const [minCapRate, setMinCapRate] = useState(0);
  const [closingTimeFilter, setClosingTimeFilter] = useState("all");
  const [occupancyFilter, setOccupancyFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  const fetchProperties = useCallback(async () => {
    if (!isAuthenticated) {
      setProperties([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/properties`, { withCredentials: true });
      setProperties(response.data.filter(p => p.status === "available"));
    } catch (err) {
      logger.error("Failed to fetch properties:", err);
      setProperties([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // Filtered properties
  const filteredProperties = useMemo(() => {
    return properties.filter(prop => {
      // Market filter
      if (marketFilter !== "all" && prop.market !== marketFilter) return false;
      
      // Listing type filter
      if (listingTypeFilter !== "all") {
        if (listingTypeFilter === "bulk" && prop.listing_type !== "bulk") return false;
        if (listingTypeFilter === "individual" && prop.listing_type === "bulk") return false;
      }
      
      // Price filter
      if (prop.price < priceRange[0] || prop.price > priceRange[1]) return false;
      
      // Cap rate filter
      if (minCapRate > 0 && (prop.cap_rate || 0) < minCapRate) return false;
      
      // Closing time filter
      if (closingTimeFilter !== "all" && prop.closing_time !== closingTimeFilter) return false;
      
      // Occupancy filter
      if (occupancyFilter !== "all" && prop.occupancy !== occupancyFilter) return false;
      
      // Search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesSearch = 
          prop.title?.toLowerCase().includes(term) ||
          prop.location?.toLowerCase().includes(term) ||
          prop.building_name?.toLowerCase().includes(term) ||
          prop.address?.toLowerCase().includes(term);
        if (!matchesSearch) return false;
      }
      
      return true;
    });
  }, [properties, marketFilter, listingTypeFilter, priceRange, minCapRate, closingTimeFilter, occupancyFilter, searchTerm]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (marketFilter !== "all") count++;
    if (listingTypeFilter !== "all") count++;
    if (priceRange[0] > 0 || priceRange[1] < 20000000) count++;
    if (minCapRate > 0) count++;
    if (closingTimeFilter !== "all") count++;
    if (occupancyFilter !== "all") count++;
    if (searchTerm) count++;
    return count;
  }, [marketFilter, listingTypeFilter, priceRange, minCapRate, closingTimeFilter, occupancyFilter, searchTerm]);

  const clearFilters = () => {
    setMarketFilter("all");
    setListingTypeFilter("all");
    setPriceRange([0, 20000000]);
    setMinCapRate(0);
    setClosingTimeFilter("all");
    setOccupancyFilter("all");
    setSearchTerm("");
  };

  const formatPrice = (price) => {
    if (!price) return "Contact for Price";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatPriceShort = (price) => {
    if (price >= 1000000) {
      return `$${(price / 1000000).toFixed(1)}M`;
    }
    return `$${(price / 1000).toFixed(0)}K`;
  };

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/portal';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-[#0066FF] text-sm font-bold uppercase tracking-wider">1031 Exchange Properties</span>
              <h1 className="text-3xl md:text-4xl font-bold text-[#1A1A2E] mt-2 mb-3" data-testid="properties-title">
                1031 Exchange Properties
              </h1>
              <p className="text-gray-600 max-w-xl">
                Curated luxury condo units in NYC and Miami for your 1031 exchange.
              </p>
            </motion.div>

            {/* Listing Type Quick Filter */}
            {isAuthenticated && (
            <div className="flex gap-2" data-testid="listing-type-filter">
              <Button
                variant={listingTypeFilter === "all" ? "default" : "outline"}
                onClick={() => setListingTypeFilter("all")}
                className={`rounded-lg text-sm ${listingTypeFilter === "all" ? "bg-[#0066FF] text-white" : ""}`}
                data-testid="filter-all-units"
              >
                All Listings
              </Button>
              <Button
                variant={listingTypeFilter === "individual" ? "default" : "outline"}
                onClick={() => setListingTypeFilter("individual")}
                className={`rounded-lg text-sm ${listingTypeFilter === "individual" ? "bg-[#0066FF] text-white" : ""}`}
                data-testid="filter-individual-units"
              >
                Individual Units
              </Button>
              <Button
                variant={listingTypeFilter === "bulk" ? "default" : "outline"}
                onClick={() => setListingTypeFilter("bulk")}
                className={`rounded-lg text-sm ${listingTypeFilter === "bulk" ? "bg-[#0066FF] text-white" : ""}`}
                data-testid="filter-bulk-units"
              >
                Bulk Condo Units
              </Button>
            </div>
            )}
          </div>
        </div>
      </section>

      {/* Filters & Listings */}
      {isAuthenticated ? (
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {/* Search Bar */}
          {isAuthenticated && (
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search by name, location, building..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 rounded-lg"
                data-testid="property-search"
              />
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          )}

          {/* Filter Bar */}
          {isAuthenticated && (
          <>
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 pb-6 border-b border-gray-100">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <Building2 className="w-5 h-5" />
                <span className="font-medium">
                  {filteredProperties.length > 0 
                    ? `${filteredProperties.length} of ${properties.length} Properties`
                    : "No Properties Available"
                  }
                </span>
              </div>
              
              {activeFilterCount > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear Filters ({activeFilterCount})
                </Button>
              )}
            </div>
            
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className={`rounded-lg ${showFilters ? 'bg-blue-50 border-blue-200' : ''}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>

          {/* Expanded Filters Panel */}
          {showFilters && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                {/* Market */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-1" />
                    Market
                  </label>
                  <Select value={marketFilter} onValueChange={setMarketFilter}>
                    <SelectTrigger className="w-full rounded-lg" data-testid="market-filter">
                      <SelectValue placeholder="All Markets" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Markets</SelectItem>
                      <SelectItem value="nyc">New York City</SelectItem>
                      <SelectItem value="miami">Miami</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Price Range */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <DollarSign className="w-4 h-4 inline mr-1" />
                    Price Range: {formatPriceShort(priceRange[0])} - {formatPriceShort(priceRange[1])}
                  </label>
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    min={0}
                    max={20000000}
                    step={100000}
                    className="mt-4"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$0</span>
                    <span>$20M+</span>
                  </div>
                </div>

                {/* Min Cap Rate */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Percent className="w-4 h-4 inline mr-1" />
                    Min Cap Rate: {minCapRate}%
                  </label>
                  <Slider
                    value={[minCapRate]}
                    onValueChange={(v) => setMinCapRate(v[0])}
                    min={0}
                    max={10}
                    step={0.5}
                    className="mt-4"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>10%</span>
                  </div>
                </div>

                {/* Closing Time */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Clock className="w-4 h-4 inline mr-1" />
                    Closing Time
                  </label>
                  <Select value={closingTimeFilter} onValueChange={setClosingTimeFilter}>
                    <SelectTrigger className="w-full rounded-lg" data-testid="closing-filter">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value="30 days">30 Days</SelectItem>
                      <SelectItem value="60 days">60 Days</SelectItem>
                      <SelectItem value="90 days">90 Days</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Occupancy */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Home className="w-4 h-4 inline mr-1" />
                    Occupancy
                  </label>
                  <Select value={occupancyFilter} onValueChange={setOccupancyFilter}>
                    <SelectTrigger className="w-full rounded-lg" data-testid="occupancy-filter">
                      <SelectValue placeholder="Any" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any</SelectItem>
                      <SelectItem value="vacant">Vacant</SelectItem>
                      <SelectItem value="rented">Rented</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </motion.div>
          )}
          </>
          )}

          {/* Property Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="spinner w-8 h-8" />
            </div>
          ) : (
            <>
              {/* Show properties or empty state */}
              {filteredProperties.length > 0 ? (
                <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProperties.map((property, idx) => (
                    <motion.div
                      key={property.property_id}
                      className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      data-testid={`property-card-${property.property_id}`}
                    >
                      {/* Image */}
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={property.images?.[0] || "https://images.unsplash.com/photo-1757621448471-cf5fcd2b84a7?w=800"}
                          alt={property.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute top-3 left-3 flex gap-2">
                          <span className="px-2 py-1 bg-white/90 backdrop-blur text-xs font-semibold rounded">
                            {property.market === "nyc" ? "NYC" : "Miami"}
                          </span>
                          {property.featured && (
                            <span className="px-2 py-1 bg-[#0066FF] text-white text-xs font-semibold rounded">
                              Featured
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <h3 className="text-lg font-bold text-[#1A1A2E] mb-1 line-clamp-1">
                          {property.title}
                        </h3>
                        <div className="flex items-center gap-1 text-gray-500 text-sm mb-3">
                          <MapPin className="w-4 h-4" />
                          <span>{property.location}</span>
                        </div>

                        {/* Details */}
                        <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                          <div className="flex items-center gap-1">
                            <Bed className="w-4 h-4" />
                            <span>{property.bedrooms} Bed</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Bath className="w-4 h-4" />
                            <span>{property.bathrooms} Bath</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Square className="w-4 h-4" />
                            <span>{property.sqft?.toLocaleString()} SF</span>
                          </div>
                        </div>

                        {/* Price & Cap Rate */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <div>
                            <span className="text-xl font-bold text-[#0066FF]">
                              {formatPrice(property.price)}
                            </span>
                            {property.cap_rate > 0 && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                Est. Cap Rate: {property.cap_rate?.toFixed(2)}%*
                              </div>
                            )}
                          </div>
                          <Link to={`/properties/${property.property_id}`}>
                            <Button variant="outline" size="sm" className="text-[#0066FF] border-[#0066FF]/30 hover:bg-blue-50">
                              Details <ArrowRight className="ml-1 w-4 h-4" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
                <p className="text-xs text-gray-400 mt-6 text-center max-w-2xl mx-auto" data-testid="cap-rate-disclaimer">
                  *Cap rate is an estimated metric based on assumptions regarding income and expenses. It is provided for informational purposes only and does not represent actual or future performance. Results may vary.
                </p>
                <p className="text-xs text-gray-400 mt-3 text-center max-w-2xl mx-auto" data-testid="listing-disclaimer">
                  Property information is provided for informational purposes only. Buyers should independently verify all details. Rental use and performance are subject to market conditions and local regulations.
                </p>
                </>
              ) : (
                /* No results */
                <div className="text-center py-16">
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                    <Lock className="w-8 h-8 text-[#0066FF]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A1A2E] mb-3">View Current Properties on the Market</h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6">
                    Register or sign in to browse our current condo unit listings with full pricing, financials, and property details.
                  </p>
                  <Button onClick={handleLogin} className="btn-primary px-8 py-5 rounded-xl font-semibold">
                    <LogIn className="w-5 h-5 mr-2" />
                    Client Login
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
      ) : (
        /* Non-authenticated: Show login gateway only */
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div 
              className="text-center py-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="max-w-2xl mx-auto">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl mx-auto mb-6 flex items-center justify-center">
                  <Lock className="w-8 h-8 text-[#0066FF]" />
                </div>
                <h3 className="text-2xl font-bold text-[#1A1A2E] mb-3" data-testid="login-gateway-title">
                  View Current Condo Unit Listings
                </h3>
                <p className="text-gray-600 max-w-lg mx-auto mb-8">
                  Register or sign in to browse potential 1031 exchange replacement condo units across NYC and Miami — with pricing, property details, and listing information.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 max-w-md mx-auto mb-8">
                  {[
                    "View available condo unit listings",
                    "Property details and building information",
                    "Compare buildings side-by-side",
                    "Coordinate with a broker",
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-gray-700 text-left">
                      <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      </div>
                      {item}
                    </div>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={handleLogin} className="btn-primary px-8 py-5 rounded-xl font-semibold text-base" data-testid="properties-login-btn">
                    <LogIn className="w-5 h-5 mr-2" />
                    Browse Properties
                  </Button>
                  <Button onClick={handleLogin} variant="outline" className="px-8 py-5 rounded-xl font-semibold text-base border-[#0066FF] text-[#0066FF] hover:bg-blue-50" data-testid="properties-register-btn">
                    Speak With a Broker
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </div>

                <p className="text-xs text-gray-400 mt-6">
                  Sign in with your Google account. All information is provided for informational purposes only. Buyers should conduct their own independent due diligence.
                </p>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Authenticated CTA */}
      {isAuthenticated && (
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-2xl font-bold text-[#1A1A2E] mb-3">
              Ready to Make a Move?
            </h2>
            <p className="text-gray-600 mb-6">
              Contact our team to discuss these properties and start your 1031 exchange.
            </p>
            <Link to="/contact">
              <Button className="btn-primary px-8 py-5 rounded-lg font-semibold">
                Contact an Agent
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
